'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { useQueryClient } from '@tanstack/react-query'
import { SubBrandName } from 'lib/settings/brands/index'
import { subBrands } from 'lib/settings/brands/sub-brands'
import { useCallback, useState } from 'react'
import { Control, useForm } from 'react-hook-form'
import {
  normalizeUsPhone,
  SMS_FINE_PRINT,
  smsConsentCheckboxCopy,
} from '../../utils/smsOptIn'
import AccountSection from '../AccountSection/AccountSection'
import Button from '../Button/Button'
import { CheckboxInput, PhoneInput } from '../Form'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'

import useRecordSmsOptInApi from '@/api/useRecordSmsOptInApi'
import { useUpdateUserApi } from '@/app/users/api/useUpdateUserApi'

// Transactional consent copy lives here, not in the cross-repo-synced
// smsOptIn util: the transactional program records consent as booleans on the
// user (UpdateUser smsTracking* fields), with no versioned consent text.
const TRANSACTIONAL_CONSENT_CHECKBOX_COPY =
  'By entering your phone number and checking the box, you agree to receive transactional text messages from {brand} (e.g., booking reminders, shipment, and tracking updates).'

const transactionalConsentCheckboxCopy = (brandName: string): string =>
  TRANSACTIONAL_CONSENT_CHECKBOX_COPY.replace('{brand}', brandName)

const TRANSACTIONAL_FINE_PRINT = {
  body: 'Consent is not a condition of purchase. Message and data rates may apply. Message frequency varies. Reply HELP for help or STOP to cancel. For additional information, view our ',
  termsLabel: 'terms/conditions',
  conjunction: ' and ',
  privacyLabel: 'privacy policy',
  suffix: '.',
}

const GENERIC_ERROR = 'Something went wrong. Please try again.'

export interface AccountSmsOptInProps {
  /**
   * Brand whose SMS programs this card writes to (page passes brand.key).
   */
  brand: SubBrandName
  /**
   * Forwarded on the marketing opt-in for backend identity stitching.
   */
  email?: string
  /**
   * Prefill for the phone field (E.164 or 10-digit).
   */
  defaultPhoneNumber?: string
  /**
   * Latched by the page at first load — row shows iff the user was not
   * already opted into the transactional program.
   */
  showTransactionalRow: boolean
  /**
   * Latched by the page once the marketing subscription query settles — row
   * shows iff the user is not already SUBSCRIBED to marketing SMS.
   */
  showMarketingRow: boolean
}

interface FinePrintCopy {
  body: string
  termsLabel: string
  conjunction: string
  privacyLabel: string
  suffix: string
}

function FinePrint({ copy }: Readonly<{ copy: FinePrintCopy }>) {
  return (
    <p className="type-legal-1 text-neutral-600">
      {copy.body}
      <Link
        target="_blank"
        rel="noreferrer"
        className="underline hover:no-underline underline-offset-1 !inline"
        href="/terms"
        inheritFont
        inline
      >
        {copy.termsLabel}
      </Link>
      {copy.conjunction}
      <Link
        target="_blank"
        rel="noreferrer"
        className="underline hover:no-underline underline-offset-1 !inline"
        href="/privacy"
        inheritFont
        inline
      >
        {copy.privacyLabel}
      </Link>
      {copy.suffix}
    </p>
  )
}

function SuccessNote({ message }: Readonly<{ message: string }>) {
  return (
    <div className="flex items-center gap-x-2">
      <Icon
        icon="check-circle-filled"
        className="w-[18px] h-[18px] text-success"
      />
      <span className="type-body-16">{message}</span>
    </div>
  )
}

/**
 * Combined My Account SMS opt-in card (GSE-12087): one phone field plus
 * independent transactional and marketing consent rows, each rendered only
 * when the user isn't already opted into that program for the current brand.
 * Owns its two mutations (vs useUser().update, which swallows success:false)
 * for per-row success/error UX; the page's useUser copy of smsTracking* stays
 * stale in-session, which is fine — row visibility is latched at first load.
 */
export default function AccountSmsOptIn({
  brand,
  email,
  defaultPhoneNumber,
  showTransactionalRow,
  showMarketingRow,
}: Readonly<AccountSmsOptInProps>) {
  const { control, handleSubmit, setError } = useForm()
  const [transactionalSuccess, setTransactionalSuccess] = useState(false)
  const [transactionalError, setTransactionalError] = useState<string | null>(
    null,
  )
  const [marketingSuccess, setMarketingSuccess] = useState(false)
  const [marketingError, setMarketingError] = useState<string | null>(null)
  const [selectionError, setSelectionError] = useState(false)

  const updateUser = useUpdateUserApi()
  const recordOptIn = useRecordSmsOptInApi()
  const queryClient = useQueryClient()

  const brandName = subBrands[brand].name
  const isPending = updateUser.isPending || recordOptIn.isPending
  const transactionalOpen = showTransactionalRow && !transactionalSuccess
  const marketingOpen = showMarketingRow && !marketingSuccess
  const allDone = !transactionalOpen && !marketingOpen

  const onFormSubmit = useCallback(
    (formData: Record<string, any>) => {
      // NANP validation happens here — PhoneInput's Controller overwrites
      // consumer validate rules with its own 10-digit check.
      const phone = normalizeUsPhone(formData.phone)
      if (!phone) {
        setError('phone', {
          type: 'validate',
          message: 'Please enter a valid US phone number.',
        })
        return
      }

      const wantsTransactional =
        transactionalOpen && !!formData.transactionalConsent
      const wantsMarketing = marketingOpen && !!formData.marketingConsent

      if (!wantsTransactional && !wantsMarketing) {
        setSelectionError(true)
        return
      }
      setSelectionError(false)

      // The two programs are independent — fire both in parallel and let each
      // row settle on its own, so one failing never blocks the other.
      if (wantsTransactional) {
        setTransactionalError(null)
        updateUser
          .mutateAsync({
            smsTrackingOptin: true,
            smsTrackingPhoneNumber: phone,
            smsTrackingTermsAccepted: true,
          })
          .then((result) => {
            if (!result.success) {
              throw new Error(
                result.errors?.[0]?.message || result.message || GENERIC_ERROR,
              )
            }
            setTransactionalSuccess(true)

            // Refresh the cached GetUser so a client-side revisit of the page
            // sees the opt-in and hides this row without a hard reload (the
            // page's visibility latch settles on the cached result).
            queryClient.invalidateQueries({ queryKey: ['user'] })
          })
          .catch((error: Error) =>
            setTransactionalError(error.message || GENERIC_ERROR),
          )
      }

      if (wantsMarketing) {
        setMarketingError(null)
        recordOptIn
          .mutateAsync({
            phone,
            smsSource: 'MY_ACCOUNT',
            email: email || undefined,
          })
          .then(() => {
            setMarketingSuccess(true)

            // Same cache sync as the transactional row: a revisit reads the
            // now-SUBSCRIBED subscription and hides this row.
            queryClient.invalidateQueries({ queryKey: ['smsSubscription'] })

            // Client-side GA4 event; the backend fires its own server-side
            // RudderStack generate_lead (channel: sms) — same split as the
            // SMS opt-in modal surfaces.
            sendGTMEvent({
              event: 'generate_lead',
              method: 'My Account SMS',
              ...(email ? { user_email: email } : {}),
            })
          })
          .catch((error: Error) =>
            setMarketingError(error.message || GENERIC_ERROR),
          )
      }
    },
    [
      email,
      marketingOpen,
      queryClient,
      recordOptIn,
      setError,
      transactionalOpen,
      updateUser,
    ],
  )

  return (
    <AccountSection title="Sign up for SMS updates and offers.">
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="flex flex-col gap-y-4 md:gap-y-6"
      >
        <PhoneInput
          name="phone"
          control={control as unknown as Control}
          label="Phone number (for shipment & tracking updates)"
          placeholder="555-555-5555"
          rules={{
            required: {
              message: 'Phone number is required.',
              value: true,
            },
          }}
          defaultValue={defaultPhoneNumber?.replace(
            /^\+?1?(\d{3})(\d{3})(\d{4})$/,
            '+1 $1-$2-$3',
          )}
          disabled={allDone}
        />
        {showTransactionalRow && (
          <div className="flex flex-col gap-y-1">
            {transactionalSuccess ? (
              <SuccessNote message="Signed up successfully" />
            ) : (
              <>
                <CheckboxInput
                  label={transactionalConsentCheckboxCopy(brandName)}
                  labelClassName="type-body-7"
                  control={control as unknown as Control}
                  name="transactionalConsent"
                  className="!items-start"
                />
                {/* Indented past the 17px box + mr-2 so the fine print sits
                    under the consent copy, per the Figma design. */}
                <div className="ml-[25px]">
                  <FinePrint copy={TRANSACTIONAL_FINE_PRINT} />
                  {transactionalError && (
                    <p className="text-red-600 text-sm">{transactionalError}</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {showMarketingRow && (
          <div className="flex flex-col gap-y-1">
            {marketingSuccess ? (
              <SuccessNote message="Almost there! Reply YES to our text to make it official." />
            ) : (
              <>
                <CheckboxInput
                  label={smsConsentCheckboxCopy(brandName)}
                  labelClassName="type-body-7"
                  control={control as unknown as Control}
                  name="marketingConsent"
                  className="!items-start"
                />
                <div className="ml-[25px]">
                  <FinePrint copy={SMS_FINE_PRINT} />
                  {marketingError && (
                    <p className="text-red-600 text-sm">{marketingError}</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {selectionError && (
          <p className="text-red-600 text-sm">
            Please check at least one box to sign up.
          </p>
        )}
        <Button
          type="submit"
          className="md:w-[278px]"
          disabled={isPending || allDone}
        >
          {isPending ? 'Signing up...' : 'Sign up'}
        </Button>
      </form>
    </AccountSection>
  )
}
